import { useState, useEffect, useCallback, useMemo } from 'react';
import { VirtualRoom } from '../services/VirtualRoomStorage';
import { databaseBridge } from '../services/DatabaseBridge';

interface UseVirtualRoomManagerOptions {
  /** The editor room's map; the editor shows and saves that map's VRs (SC-422). */
  mapId: string;
}

export function useVirtualRoomManager(options: UseVirtualRoomManagerOptions) {
  const { mapId } = options;
  const [rooms, setRooms] = useState<VirtualRoom[]>([]);
  const [activeRoom, setActiveRoom] = useState<VirtualRoom | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use the global databaseBridge instance instead of creating a new VirtualRoomStorage
  const storage = useMemo(() => databaseBridge, []);

  // Initialize storage and load rooms
  useEffect(() => {
    const initStorage = async () => {
      try {
        setLoading(true);
        setError(null);
        await storage.initialize(mapId);
        const loadedRooms = await storage.getRooms();
        setRooms(loadedRooms);
        
        // Don't automatically activate rooms during initialization
        // Rooms should only be activated when explicitly selected by the user
        // This prevents automatic edit mode activation on plugin load
        setActiveRoom(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize room storage';
        setError(errorMessage);
        console.error('[useVirtualRoomManager] Storage initialization error:', err);
      } finally {
        setLoading(false);
      }
    };

    initStorage();
  }, [storage, mapId]);

  const createRoom = useCallback(async (roomData: Omit<VirtualRoom, 'id' | 'createdAt' | 'updatedAt' | 'isActive'>) => {
    try {
      setError(null);
      const newRoom: VirtualRoom = {
        ...roomData,
        mapId,
        id: `room_${Date.now()}_${Math.random().toString(36).substr(2, 9).replace(/[.]/g, '_')}`,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        isActive: false,
      };

      await storage.createRoom(newRoom);
      setRooms(prev => [...prev, newRoom]);
      
      // Don't automatically activate the first room
      // Let the user explicitly choose which room to activate
      // This prevents automatic edit mode activation

      return newRoom;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create room';
      setError(errorMessage);
      console.error('[useVirtualRoomManager] Create room error:', err);
      throw err;
    }
  }, [storage, mapId]);

  const updateRoom = useCallback(async (roomId: string, updates: Partial<VirtualRoom>) => {
    try {
      setError(null);
      await storage.updateRoom(roomId, updates);
      
      setRooms(prev => prev.map(room => 
        room.id === roomId ? { ...room, ...updates, updatedAt: Date.now() } : room
      ));

      if (activeRoom?.id === roomId) {
        setActiveRoom(prev => prev ? { ...prev, ...updates, updatedAt: Date.now() } : null);
      }

      console.log('[useVirtualRoomManager] Room updated:', roomId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update room';
      setError(errorMessage);
      console.error('[useVirtualRoomManager] Update room error:', err);
      throw err;
    }
  }, [storage, activeRoom]);

  /** Drops a VR from the list after the plugin deleted it (one Cloud Code call, in the plugin). */
  const forgetRoom = useCallback((roomId: string) => {
    setRooms(prev => prev.filter(room => room.id !== roomId));
    if (activeRoom?.id === roomId) {
      setActiveRoom(null);
    }
  }, [activeRoom]);

  const activateRoom = useCallback(async (room: VirtualRoom | null) => {
    try {
      setError(null);
      
      if (room) {
        // Deactivate all rooms first
        await storage.setActiveRoom(room.id);
        
        // Update local state
        setRooms(prev => prev.map(r => ({ ...r, isActive: r.id === room.id })));
        setActiveRoom(room);
      } else {
        // Deactivate all rooms
        setRooms(prev => prev.map(r => ({ ...r, isActive: false })));
        setActiveRoom(null);
      }

      console.log('[useVirtualRoomManager] Active room set to:', room?.id || 'none');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to set active room';
      setError(errorMessage);
      console.error('[useVirtualRoomManager] Set active room error:', err);
      throw err;
    }
  }, [storage]);

  const refreshRooms = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const loadedRooms = await storage.getRooms();
      setRooms(loadedRooms);
      
      // Don't automatically activate rooms during refresh
      // Keep the current active room state unless explicitly changed
      // This prevents automatic edit mode activation when rooms are refreshed
      
      console.log('[useVirtualRoomManager] Rooms refreshed:', loadedRooms.length);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh rooms';
      setError(errorMessage);
      console.error('[useVirtualRoomManager] Refresh rooms error:', err);
    } finally {
      setLoading(false);
    }
  }, [storage]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    rooms,
    activeRoom,
    loading,
    error,
    mapId,
    
    // Actions
    createRoom,
    updateRoom,
    forgetRoom,
    activateRoom,
    refreshRooms,
    clearError,
  };
} 